import PDFKit from 'pdfkit';
import {
  col4,
  COL4_WIDTH,
  COLOR_LIGHT,
  colorKeyBlock,
  CONTENT_WIDTH,
  deref,
  FONT,
  FONT_BOLD,
  PAGE_MARGINS,
  SIZE_DESCRIPTION,
} from '@/lib/pdf/helpers';
import { BarGraph, listAllPayoutCategories, toMonthlyGraph } from '@/lib/graphs';
import { CalculationState } from '@/lib/calculationstate';

import { getPayoutColor, getPayoutName } from '@/lib/payout';
import { format as formatCurrency } from '@/lib/currency';

const BOX_HEIGHT = 250;
const BOX_PAD = 15;

const BAR_WIDTH = COL4_WIDTH / 2.5;
const BAR_HEIGHT = 0.8 * BOX_HEIGHT;

const KEY_SIZE = 30;

// Disable param reassign because working with doc becomes horrific otherwise
/* eslint no-param-reassign: 0 */

/**
 * Calculates the height of a single segment on the graph
 *
 * @param value
 * @param max
 */
function segmentHeight(value: number, max: number) {
  return Math.round(BAR_HEIGHT * (value / max));
}

/**
 * Draws a graph bar segment in the box indicated by index.
 *
 * @param doc PDF document
 * @param index Index of the graph (0..3)
 * @param y Current bottom of the graph stack
 * @param value Value to draw column of
 * @param max Maximum value of total graph
 * @param color Color for segment
 *
 * @returns the new y coordinate to be used in subsequent calls to stack the chart
 */
function drawSegment(
  doc: PDFKit.PDFDocument,
  index: number,
  y: number,
  value: number,
  max: number,
  color: string,
): number {
  const height = segmentHeight(value, max);

  const x = col4(index) + ((COL4_WIDTH - BAR_WIDTH) / 2);

  doc
    .save()
    .fillColor(color)
    .rect(x, y - height, BAR_WIDTH, height)
    .fill()
    .restore();

  return y - height;
}

/**
 * Draws a single multi-segment bar for the barchart.
 *
 * Expects graphY to be the top of the box, and index to be the number of the graph to be rendered.
 *
 * After calling this function, the position of the cursor is undefined.
 *
 * @param doc Document to render into
 * @param graphY Top of the drawing box for this bar
 * @param index Index of the graph that is being drawn. Used for X positions
 * @param state Calculation state
 * @param graph Graph calculations to render
 */
function graphBar(doc: PDFKit.PDFDocument, graphY: number, index: number, state: CalculationState, graph: BarGraph) {
  doc.save();

  let y = graphY + BOX_HEIGHT;

  const maxValue = state.salary / 12;
  const totalValue = graph.segments.reduce((acc, segment) => acc + deref(segment.value), 0);
  const totalPercent = Math.round((totalValue / maxValue) * 100);

  doc
    .fontSize(SIZE_DESCRIPTION)
    .font(FONT)
    .text(`${totalPercent}%`, col4(index), graphY + 10, { align: 'center', width: COL4_WIDTH })
    .font(FONT_BOLD)
    .text(formatCurrency(totalValue), { align: 'center', width: COL4_WIDTH })
    .text('bruto per maand', { align: 'center', width: COL4_WIDTH });

  graph.segments
    .filter((segment) => deref(segment.value) > 0)
    .forEach((segment) => {
      y = drawSegment(doc, index, y, deref(segment.value), maxValue, getPayoutColor(segment.category));
    });

  doc.restore();
}

/**
 * Draws the barchart and barchart legend for the first page of the report.
 *
 * After calling this function the cursor will be positioned at the bottom-left of the drawn legend.
 *
 * @param doc
 * @param state
 * @param yearlyGraphs
 */
export default function drawGraphs(
  doc: PDFKit.PDFDocument,
  state: CalculationState,
  yearlyGraphs: BarGraph[],
) {
  doc
    .moveDown()
    .font(FONT)
    .fontSize(13)
    .text('Inkomen na arbeidsongeschiktheid tot maximaal AOW-leeftijd', PAGE_MARGINS)
    .moveDown();

  doc.save();
  let { y } = doc;
  doc
    .lineWidth(1)
    .strokeColor(COLOR_LIGHT)
    .rect(col4(0), y, COL4_WIDTH, BOX_HEIGHT)
    .rect(col4(1), y, COL4_WIDTH, BOX_HEIGHT)
    .rect(col4(2), y, COL4_WIDTH, BOX_HEIGHT)
    .rect(col4(3), y, COL4_WIDTH, BOX_HEIGHT)
    .stroke();

  const graphs = yearlyGraphs.map(toMonthlyGraph);

  graphs.forEach((graph, i) => {
    graphBar(doc, y, i, state, graph);
  });

  y += BOX_HEIGHT;

  let maxHeight = 0;
  graphs.forEach((graph, i) => {
    doc
      .font(FONT)
      .fontSize(SIZE_DESCRIPTION)
      .text(deref(graph.name), col4(i) + BOX_PAD, y + 10, {
        align: 'center',
        width: COL4_WIDTH - (2 * BOX_PAD),
      })
      .moveDown();
    maxHeight = Math.max(maxHeight, doc.y - y);
  });

  // Slight nudge to make it look nicer
  maxHeight -= 5;
  doc
    .rect(col4(0), y, COL4_WIDTH, maxHeight)
    .rect(col4(1), y, COL4_WIDTH, maxHeight)
    .rect(col4(2), y, COL4_WIDTH, maxHeight)
    .rect(col4(3), y, COL4_WIDTH, maxHeight)
    .stroke();

  y += maxHeight + BOX_PAD;

  const categories = listAllPayoutCategories(graphs);
  categories.forEach((category, i) => {
    doc.x = PAGE_MARGINS + (i % 3) * (CONTENT_WIDTH / 3);
    doc.y = y + Math.floor(i / 3) * (KEY_SIZE + 10);

    colorKeyBlock(doc, getPayoutColor(category), getPayoutName(category), null, {
      squareSize: KEY_SIZE,
      fontSize: 8,
      width: CONTENT_WIDTH / 3,
    });
  });

  doc.x = PAGE_MARGINS;

  doc.restore();
}
